<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignV2List from './CampaignV2List.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const activeTab = ref('whatsapp');

const uiFlags = computed(() => store.getters['campaignsV2/getUIFlags']);
const isFetching = computed(() => uiFlags.value.isFetching);

const whatsAppCampaigns = computed(
  () => store.getters['campaignsV2/getWhatsAppCampaigns']
);
const apiCampaigns = computed(
  () => store.getters['campaignsV2/getApiCampaigns']
);

const currentCampaigns = computed(() =>
  activeTab.value === 'whatsapp' ? whatsAppCampaigns.value : apiCampaigns.value
);

const isEmpty = computed(
  () => currentCampaigns.value?.length === 0 && !isFetching.value
);

const openWizard = () => {
  router.push({ name: 'campaigns_new', params: route.params });
};

const viewAnalytics = campaign => {
  router.push({
    name: 'campaigns_analytics',
    params: { ...route.params, campaignId: campaign.id },
  });
};

const editCampaign = campaign => {
  router.push({
    name: 'campaigns_edit',
    params: { ...route.params, campaignId: campaign.id },
  });
};

const refreshList = () => {
  store.dispatch('campaignsV2/get');
};

onMounted(() => {
  store.dispatch('campaignsV2/get');
  store.dispatch('audiences/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('CAMPAIGN.V2.HEADER_TITLE') }}
      </h1>
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 transition-colors"
          :disabled="isFetching"
          @click="refreshList"
        >
          <span class="text-base leading-none">&#8635;</span>
          {{ t('CAMPAIGN.V2.REFRESH') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
          @click="openWizard"
        >
          {{ t('CAMPAIGN.V2.NEW_CAMPAIGN') }}
        </button>
      </div>
    </div>

    <div class="flex gap-2 px-6 pt-4">
      <button
        class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
        :class="
          activeTab === 'whatsapp'
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="activeTab = 'whatsapp'"
      >
        {{ t('CAMPAIGN.V2.TAB_WHATSAPP') }}
      </button>
      <button
        class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
        :class="
          activeTab === 'api'
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="activeTab = 'api'"
      >
        {{ t('CAMPAIGN.V2.TAB_API') }}
      </button>
    </div>

    <div class="flex-1 px-6 py-4">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <CampaignV2List
        v-else-if="!isEmpty"
        :campaigns="currentCampaigns"
        @view-analytics="viewAnalytics"
        @edit="editCampaign"
      />
      <div
        v-else
        class="flex flex-col items-center justify-center py-20 text-center"
      >
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ t('CAMPAIGN.V2.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.V2.EMPTY_STATE.SUBTITLE') }}
        </p>
      </div>
    </div>
  </div>
</template>
